import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  buildPathToBreadcrumbMapping,
  getBreadcrumbForPath,
  parseBreadcrumbToSegments,
  storeBreadcrumbState,
  getStoredBreadcrumbState
} from "../Utils/breadcrumbUtils";

/**
 * Custom hook for managing breadcrumb navigation
 * 
 * Features:
 * - Syncs breadcrumb with URL (handles back/forward navigation)
 * - Persists state across refresh using sessionStorage
 * - Provides clickable breadcrumb segments
 * - Handles dynamic menu data from Redux
 * 
 * @returns {Object} {
 *   pagePath: string,           // Display string "Admin > Employee > New Employee"
 *   breadcrumbSegments: Array,  // Clickable segments with paths
 *   setPagePath: function,      // Manual setter (for menu clicks)
 *   navigateToSegment: function // Navigate to a breadcrumb segment
 * }
 */
const useBreadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get menu data from Redux store
  const { data } = useSelector((state) => state.landing);
  const menuData = data?.client_schema_name?.[2] || [];
  
  // Build path mapping whenever menu data changes
  const pathMapping = useMemo(() => {
    return buildPathToBreadcrumbMapping(menuData);
  }, [menuData]);
  
  // Initialize pagePath from stored state or derive from URL
  const [pagePath, setPagePathState] = useState(() => {
    // First, try to get from sessionStorage (survives refresh)
    const storedState = getStoredBreadcrumbState();
    
    // If stored state matches current URL, use it
    if (storedState && storedState.urlPath === location.pathname) {
      return storedState.breadcrumb;
    }
    
    // Otherwise, default to "Home" - will be updated by useEffect
    return "Home";
  });

  // Sync pagePath with URL changes (handles back/forward navigation)
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Special case for root path
    if (currentPath === "/" || currentPath === "") {
      setPagePathState("Home");
      storeBreadcrumbState("/", { breadcrumb: "Home" });
      return;
    }
    
    // Look up breadcrumb info for current URL
    const breadcrumbInfo = getBreadcrumbForPath(currentPath, pathMapping);
    
    if (breadcrumbInfo) {
      setPagePathState(breadcrumbInfo.breadcrumb);
      storeBreadcrumbState(currentPath, breadcrumbInfo);
    } else {
      // URL not found in mapping - could be a dynamic route or unmapped page
      // Try to get from sessionStorage as fallback
      const storedState = getStoredBreadcrumbState();
      if (storedState && storedState.urlPath === currentPath) {
        setPagePathState(storedState.breadcrumb);
      }
      // If still not found, keep current pagePath (might be set manually)
    }
  }, [location.pathname, pathMapping]);

  // Parse breadcrumb into clickable segments
  const breadcrumbSegments = useMemo(() => {
    return parseBreadcrumbToSegments(pagePath, pathMapping, menuData);
  }, [pagePath, pathMapping, menuData]);

  // Manual setter for pagePath (used when clicking menu items)
  const setPagePath = useCallback((newPath) => {
    setPagePathState(newPath);
    storeBreadcrumbState(location.pathname, { breadcrumb: newPath });
  }, [location.pathname]);

  // Navigate to a breadcrumb segment
  const navigateToSegment = useCallback((segment) => {
    if (segment.path && segment.isClickable) {
      navigate(segment.path);
    }
  }, [navigate]);

  return {
    pagePath,
    breadcrumbSegments,
    setPagePath,
    navigateToSegment,
    pathMapping // Expose for debugging or advanced use
  };
};

export default useBreadcrumb;